import { RollbackOutlined } from '@ant-design/icons';
import { Spin, Tag } from 'antd';
import { StyledThemeDetail } from './ThemeDetail.styled';
import useThemeDetail from './useThemeDetail.hook';
import Link from 'next/link';
import TaskTemplates from './TaskTemplates/TaskTemplates';
import Activity from '@/shared/Activities/Activity';
import ThemeActivitiesLinks from '@/shared/ThemeActivities/ThemeActivitiesLinks';
import Button from '@/shared/Button/Button';
import LibraryTopbar from '@/pages/Library/LibraryTopbar/LibraryTopbar';
import LibraryLevelFilter from '@/pages/Library/LibraryLevelFilter/LibraryLevelFilter';
import EditTheme from '@/pages/Library/LibraryThemes/EditTheme/EditTheme';
import AddThemes from '@/pages/Library/LibraryThemes/AddThemes/AddThemes';
import useLibraryUtils from '@/pages/Library/useLibraryUtils.hook';
import ExploreAndPlay from '../ExploreAndPlay/ExploreAndPlay';
import {
  ExpandedScreenContextProvider,
  useExpandedScreenContext,
} from './ExpandedScreen/ExpandedScreenContext';
import ActivityButtons from '../Activities/ActivityComponents/ActivityButtons/ActivityButtons';
import { useMediaQuery } from 'react-responsive';
import MediaQueryBreakpoints from '@/constants/MediaQueryBreakpoints';

export default function ThemeDetail(props) {
  return (
    <ExpandedScreenContextProvider>
      <ThemeDetailContent {...props} />
    </ExpandedScreenContextProvider>
  );
}

function ThemeDetailContent({
  serverSideTheme,
  serverSideActivity,
  activityType,
}) {
  const expandedScreenContext = useExpandedScreenContext();
  const { state, actions } = useThemeDetail({ activityType });
  const { showAddButtons, showTaskTemplates, oneClass, isLibrary, isTeacher } =
    useLibraryUtils();

  const theme = state.theme || serverSideTheme;
  const selectedActivity = state.selectedActivity || serverSideActivity;

  const isSmall = useMediaQuery({
    maxWidth: MediaQueryBreakpoints.md,
  });

  return (
    <StyledThemeDetail
      isLibrary={isLibrary}
      className={expandedScreenContext.state.active ? 'expanded-screen' : ''}
    >
      <LibraryTopbar
        currentTheme={theme}
        backButton={
          <Link href={isLibrary ? '/library' : '/themes'}>
            <a>
              <Button size="small" icon={<RollbackOutlined />}>
                <span>Back to {isLibrary ? 'library' : 'themes'}</span>
              </Button>
            </a>
          </Link>
        }
      />
      <LibraryLevelFilter
        fullText="All levels"
        size="small"
        currentTheme={theme}
      />

      <Spin spinning={state.loading}>
        {theme && selectedActivity ? (
          <div className="content-wrapper">
            <div className="theme-activities-and-preview">
              <div className="activity-detail">
                <div className="activity-preview">
                  <Activity
                    className={selectedActivity.type}
                    key={selectedActivity.id}
                    activity={selectedActivity}
                    isPreview
                  />
                </div>
                <div className="activity-info">
                  <div
                    className="activity-description"
                    dangerouslySetInnerHTML={{
                      __html: selectedActivity.shortDescription,
                    }}
                  ></div>
                  <div className="activity-buttons">
                    <ActivityButtons activity={selectedActivity} />
                    {showAddButtons ? (
                      state.loadingTheme ? (
                        <Spin />
                      ) : state.themeInClass?.themeEnabled ? (
                        oneClass || isTeacher ? (
                          <Button
                            className="edit-theme-button"
                            onClick={() =>
                              actions.setSelectedAction('editTheme')
                            }
                          >
                            Edit theme for&nbsp;
                            <ExploreAndPlay />
                          </Button>
                        ) : (
                          <Tag color="green" className="added-tag">
                            Added to&nbsp;
                            <ExploreAndPlay />
                          </Tag>
                        )
                      ) : (
                        <Button
                          type="primary"
                          ghost
                          onClick={() => actions.setSelectedAction('addTheme')}
                        >
                          Add theme to&nbsp;
                          <ExploreAndPlay />
                        </Button>
                      )
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="activities-container">
                <ThemeActivitiesLinks
                  themeActivities={theme.activities}
                  linksPrefix={isLibrary ? '/library/activities' : ''}
                  selectedActivityId={state.selectedActivity?.id}
                  layout={isSmall ? 'column' : 'row'}
                />
              </div>
            </div>
            {showTaskTemplates ? (
              <TaskTemplates
                theme={theme}
                themeInClass={state.themeInClass}
                onFinishAction={actions.loadThemeEnabledForClasses}
              />
            ) : null}
            {!showTaskTemplates && !isLibrary ? (
              <div
                className="public-description"
                dangerouslySetInnerHTML={{
                  __html: selectedActivity.description,
                }}
              ></div>
            ) : null}
          </div>
        ) : null}
      </Spin>
      {state.selectedAction &&
        {
          addTheme: (
            <AddThemes
              themes={[theme]}
              onClose={() => actions.setSelectedAction(null)}
              onFinish={() => {
                actions.setSelectedAction(null);
                actions.loadThemeEnabledForClasses();
              }}
            />
          ),
          editTheme: (
            <EditTheme
              theme={theme}
              onClose={() => actions.setSelectedAction(null)}
              onFinish={(themeDeleted) => {
                actions.setSelectedAction(null);
                if (themeDeleted) {
                  actions.loadThemeEnabledForClasses();
                }
              }}
            />
          ),
        }[state.selectedAction]}
    </StyledThemeDetail>
  );
}
